import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    // Initialize socket connection
    const SOCKET_URL =
      import.meta.env.VITE_SOCKET_URL || "http://localhost:5001";

    const newSocket = io(SOCKET_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    newSocket.on("connect", () => {
      setIsConnected(true);
    });

    newSocket.on("disconnect", (reason) => {
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setIsConnected(false);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []);

  const joinOrganization = (organizationId) => {
    if (socket && organizationId) {
      socket.emit("join-organization", organizationId);
    }
  };

  const leaveOrganization = (organizationId) => {
    if (socket && organizationId) {
      socket.emit("leave-organization", organizationId);
    }
  };

  const joinProject = (projectId) => {
    if (socket && projectId) {
      socket.emit("join-project", projectId);
    }
  };

  const leaveProject = (projectId) => {
    if (socket && projectId) {
      socket.emit("leave-project", projectId);
    }
  };

  const joinConversation = (conversationId) => {
    if (socket && conversationId) {
      socket.emit("join-conversation", conversationId);
    }
  };

  const leaveConversation = (conversationId) => {
    if (socket && conversationId) {
      socket.emit("leave-conversation", conversationId);
    }
  };

  const joinUser = (userId) => {
    if (socket && userId) {
      socket.emit("join-user", userId);
    }
  };

  const leaveUser = (userId) => {
    if (socket && userId) {
      socket.emit("leave-user", userId);
    }
  };

  const value = {
    socket,
    isConnected,
    joinOrganization,
    leaveOrganization,
    joinProject,
    leaveProject,
    joinConversation,
    leaveConversation,
    joinUser,
    leaveUser,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
